import { Component, type ErrorInfo, type ReactNode } from 'react';

import { captureReactBoundaryError } from '../lib/observability/sentry';
import { enqueueError } from '../services/errorTracking';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Error boundary genérico. Captura erros de render em componentes filhos
 * e exibe um fallback em vez de desmontar a árvore React inteira.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Reportar para observabilidade. window.onerror NÃO captura erros de
    // render React (são engolidos internamente e re-emitidos via boundary),
    // então este é o único ponto onde conseguimos enfileirar o componentStack
    // — o sinal mais útil para identificar qual componente travou em prod.
    try {
      enqueueError({
        message: error.message,
        stack: error.stack,
        component_stack: info.componentStack ?? undefined,
        url: window.location.href,
        user_agent: navigator.userAgent,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timestamp: new Date().toISOString(),
      });
    } catch {
      // Nunca deixar o reporter quebrar o fallback do boundary.
    }
    try {
      captureReactBoundaryError(error, info.componentStack ?? undefined);
    } catch {
      // Sentry pode falhar silenciosamente (sem rede, DSN inválido) — não derrubar o fallback.
    }
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          fontFamily: 'Inter, system-ui, sans-serif',
          background: '#1C1C1C',
          color: '#ECECEC',
        }}>
          <div style={{ maxWidth: 480, textAlign: 'center' }}>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.75rem' }}>
              Algo deu errado
            </h1>
            <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginBottom: '1.5rem' }}>
              {this.state.error.message}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '0.5rem 1.5rem',
                borderRadius: '0.375rem',
                border: '1px solid #444',
                background: 'transparent',
                color: '#ECECEC',
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              Recarregar página
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
